import { useState, useRef, useCallback } from "react";
// CAPACITOR REMOVED: Mobile app is now handled by billposter-expo
// This hook now only supports web camera access via getUserMedia

interface UsePhotoCaptureOptions {
  maxPhotos?: number;
  quality?: number;
  maxWidth?: number;
  maxHeight?: number;
}

interface CapturedPhoto {
  id: string;
  file: File;
  preview: string;
  timestamp: Date;
}

export function usePhotoCapture(options: UsePhotoCaptureOptions = {}) {
  const {
    maxPhotos = 5,
    quality = 0.8,
    maxWidth = 1920,
    maxHeight = 1080,
  } = options;

  const [photos, setPhotos] = useState<CapturedPhoto[]>([]);
  const [isCapturing, setIsCapturing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingPhoto, setPendingPhoto] = useState<CapturedPhoto | null>(null); // Photo waiting for confirmation
  const fileInputRef = useRef<HTMLInputElement>(null);

  const resizeImage = useCallback((file: File): Promise<File> => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        // Calculate new dimensions
        let { width, height } = img;

        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height);
          width *= ratio;
          height *= ratio;
        }

        canvas.width = width;
        canvas.height = height;

        // Draw and compress
        ctx?.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (blob) {
              const resizedFile = new File([blob], file.name, {
                type: 'image/jpeg',
                lastModified: Date.now(),
              });
              resolve(resizedFile);
            } else {
              resolve(file);
            }
          },
          'image/jpeg',
          quality
        );
      };

      img.src = URL.createObjectURL(file);
    });
  }, [maxWidth, maxHeight, quality]);

  const captureFromCamera = useCallback(async () => {
    if (photos.length >= maxPhotos) {
      setError(`Maximum ${maxPhotos} photos allowed`);
      return;
    }

    setIsCapturing(true);
    setError(null);

    try {
      // CAPACITOR REMOVED: Native mobile camera is now handled by billposter-expo
      // Web camera capture using getUserMedia
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: 'environment' },
          width: { ideal: maxWidth },
          height: { ideal: maxHeight },
        },
      });

      const video = document.createElement('video');
      video.srcObject = stream;
      video.play();

      // Wait for user to click/confirm before capturing
      const captureFrame = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        ctx?.drawImage(video, 0, 0);

        canvas.toBlob(
          (blob) => {
            if (blob) {
              const file = new File([blob], `photo_${Date.now()}.jpg`, {
                type: 'image/jpeg',
                lastModified: Date.now(),
              });

              const photo: CapturedPhoto = {
                id: `photo_${Date.now()}`,
                file,
                preview: URL.createObjectURL(blob),
                timestamp: new Date(),
              };

              setPhotos(prev => [...prev, photo]);
            }

            stream.getTracks().forEach(track => track.stop());
            setIsCapturing(false);
          },
          'image/jpeg',
          quality
        );
      };

      // For web, capture immediately
      video.addEventListener('loadedmetadata', () => {
        // Small delay to ensure video is ready
        setTimeout(captureFrame, 100);
      });
    } catch (err) {
      console.error('Camera access error:', err);
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';

      // Provide more specific error messages
      if (errorMessage.includes('permission') || errorMessage.includes('Permission')) {
        setError('Camera permission denied. Please enable camera access in app settings.');
      } else if (errorMessage.includes('cancelled') || errorMessage.includes('cancel')) {
        setError('Photo capture cancelled.');
      } else {
        setError(`Failed to access camera: ${errorMessage}. Please check permissions and try again.`);
      }

      setIsCapturing(false);
    }
  }, [photos.length, maxPhotos, maxWidth, maxHeight, quality, resizeImage]);

  const captureFromFile = useCallback(async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    if (photos.length + files.length > maxPhotos) {
      setError(`Maximum ${maxPhotos} photos allowed`);
      return;
    }

    setError(null);

    try {
      const photoPromises = Array.from(files).map(async (file) => {
        if (!file.type.startsWith('image/')) {
          throw new Error('Please select only image files');
        }

        const resizedFile = await resizeImage(file);

        const photo: CapturedPhoto = {
          id: `photo_${Date.now()}_${Math.random()}`,
          file: resizedFile,
          preview: URL.createObjectURL(resizedFile),
          timestamp: new Date(),
        };

        return photo;
      });

      const newPhotos = await Promise.all(photoPromises);
      // For single photo mode, show preview. For multiple, add all immediately
      if (maxPhotos === 1 && newPhotos.length === 1) {
        setPendingPhoto(newPhotos[0]);
      } else {
        setPhotos(prev => [...prev, ...newPhotos]);
      }
    } catch (err) {
      console.error('File processing error:', err);
      setError(err instanceof Error ? err.message : 'Failed to process image files');
    }
  }, [photos.length, maxPhotos, resizeImage]);

  const removePhoto = useCallback((photoId: string) => {
    setPhotos(prev => {
      const photo = prev.find(p => p.id === photoId);
      if (photo) {
        URL.revokeObjectURL(photo.preview);
      }
      return prev.filter(p => p.id !== photoId);
    });
  }, []);

  const clearPhotos = useCallback(() => {
    photos.forEach(photo => URL.revokeObjectURL(photo.preview));
    setPhotos([]);
    setError(null);
  }, [photos]);

  const triggerFileInput = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  // Confirm pending photo - add it to photos array
  const confirmPendingPhoto = useCallback(() => {
    if (pendingPhoto) {
      setPhotos(prev => [...prev, pendingPhoto]);
      setPendingPhoto(null);
    }
  }, [pendingPhoto]);

  // Reject pending photo - discard it
  const rejectPendingPhoto = useCallback(() => {
    if (pendingPhoto) {
      URL.revokeObjectURL(pendingPhoto.preview);
      setPendingPhoto(null);
    }
  }, [pendingPhoto]);

  return {
    photos,
    isCapturing,
    error,
    fileInputRef,
    captureFromCamera,
    captureFromFile,
    removePhoto,
    clearPhotos,
    triggerFileInput,
    canAddMore: photos.length < maxPhotos,
    pendingPhoto, // Photo waiting for confirmation
    confirmPendingPhoto, // Call to accept the photo
    rejectPendingPhoto, // Call to discard the photo
  };
}
